import React from "react";
import { ErrorBanner, FONT, NAME_MAX, Spinner, fieldBox, fieldHeight, h2Style, inputStyle, isValidName, labelStyle, primaryButton, rise, subStyle } from "./authFlowUi";

const ROLES = [
  "Founder / executive",
  "Proposal / bid manager",
  "Sales or business development",
  "Operations",
  "Legal or compliance",
  "Other",
];
const SOURCES = ["Google search", "LinkedIn", "A colleague or referral", "Event or webinar", "Other"];
const USES = [
  { id: "tenders", title: "Win tenders", sub: "Find, qualify and write bids" },
  { id: "extract", title: "Extract data", sub: "Contracts, invoices, forms" },
  { id: "both", title: "Both", sub: "The full platform" },
];

const PHONE_CHARS = /^[0-9+\-(). ]+$/;
const isValidPhone = (value) => {
  const v = value.trim();
  const digits = v.replace(/\D/g, "").length;
  return v.length <= 32 && PHONE_CHARS.test(v) && digits >= 7 && digits <= 15;
};

const Chevron = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8B7D91"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Select = ({ id, value, onChange, options, height }) => (
  <div style={{ position: "relative" }}>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height,
        padding: "0 38px 0 16px",
        boxSizing: "border-box",
        borderRadius: 12,
        background: "#fff",
        appearance: "none",
        WebkitAppearance: "none",
        outline: "none",
        cursor: "pointer",
        font: `500 14.5px ${FONT}`,
        ...(value
          ? { color: "#171717", border: "1.5px solid rgba(139,85,221,.45)" }
          : { color: "#A79AAD", border: "1.5px solid rgba(0,0,0,.10)" }),
      }}
    >
      <option value="" disabled>
        Select…
      </option>
      {options.map((o) => (
        <option key={o} value={o} style={{ color: "#171717" }}>
          {o}
        </option>
      ))}
    </select>
    <Chevron />
  </div>
);

const StepAbout = ({
  mobile,
  short,
  first,
  showName,
  name,
  onNameChange,
  company,
  phone,
  role,
  heard,
  use,
  updates,
  onChange,
  loading,
  error,
  onStart,
  onSkip,
}) => {
  const H = fieldHeight(mobile, short);
  const nameOk = !showName || isValidName(name);
  const phoneOk = !phone.trim() || isValidPhone(phone);
  const canStart = !!(nameOk && phoneOk && company.trim() && role && heard && use && !loading);

  const companyField = (
    <div style={fieldBox(company.trim() ? "filled" : "idle", H)}>
      <input
        id="ds-company"
        type="text"
        value={company}
        onChange={(e) => onChange({ company: e.target.value })}
        placeholder="Acme Inc."
        autoComplete="organization"
        style={inputStyle("0 16px")}
      />
    </div>
  );

  return (
    <div style={rise}>
      <h2 style={h2Style}>Tell us about you</h2>
      <p style={subStyle(mobile || short ? "6px 0 14px" : "8px 0 20px")}>
        {mobile
          ? `Nice to meet you, ${first}. This takes 20 seconds.`
          : `Nice to meet you, ${first}. A few quick details help us tailor DocuSensa to your team.`}
      </p>

      {showName ? (
        <div style={{ display: "grid", gap: mobile ? 10 : 12, gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>
          <div>
            <label htmlFor="ds-about-name" style={labelStyle}>Your name</label>
            <div style={fieldBox(nameOk ? "filled" : "bad", H)}>
              <input
                id="ds-about-name"
                type="text"
                value={name}
                onChange={(e) => onNameChange(e.target.value.replace(/\s{2,}/g, " "))}
                placeholder="Enter your name here"
                autoComplete="name"
                maxLength={NAME_MAX}
                aria-invalid={!nameOk}
                aria-describedby={nameOk ? undefined : "ds-about-name-hint"}
                style={inputStyle("0 16px")}
              />
            </div>
            {!nameOk && (
              <p id="ds-about-name-hint" style={{ margin: "6px 0 0", font: `500 12px ${FONT}`, color: "#E0476B" }}>
                {name.trim() ? "Use letters only, no numbers or symbols." : "Add your name so your team knows who's who."}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="ds-company" style={labelStyle}>Company name</label>
            {companyField}
          </div>
        </div>
      ) : (
        <>
          <label htmlFor="ds-company" style={labelStyle}>Company name</label>
          {companyField}
        </>
      )}

      <label htmlFor="ds-phone" style={{ ...labelStyle, marginTop: mobile || short ? 12 : 16 }}>
        Phone number <span style={{ fontWeight: 400, color: "#71717A" }}>(optional)</span>
      </label>
      <div style={fieldBox(!phoneOk ? "bad" : phone.trim() ? "filled" : "idle", H)}>
        <input
          id="ds-phone"
          type="tel"
          value={phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          placeholder="+1 555 010 0100"
          autoComplete="tel"
          inputMode="tel"
          maxLength={32}
          aria-invalid={!phoneOk}
          aria-describedby={phoneOk ? undefined : "ds-phone-hint"}
          style={inputStyle("0 16px")}
        />
      </div>
      {!phoneOk && (
        <p id="ds-phone-hint" style={{ margin: "6px 0 0", font: `500 12px ${FONT}`, color: "#E0476B" }}>
          Use digits, spaces and + - ( ) only, 7 to 15 digits.
        </p>
      )}

      <div
        style={{
          display: "grid",
          gap: mobile ? 10 : 12,
          alignItems: "end",
          marginTop: mobile || short ? 12 : 16,
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
        }}
      >
        <div>
          <label htmlFor="ds-role" style={labelStyle}>Your role</label>
          <Select id="ds-role" value={role} onChange={(v) => onChange({ role: v })} options={ROLES} height={H} />
        </div>
        <div>
          <label htmlFor="ds-heard" style={labelStyle}>{mobile ? "Found us via" : "How did you hear about us?"}</label>
          <Select id="ds-heard" value={heard} onChange={(v) => onChange({ heard: v })} options={SOURCES} height={H} />
        </div>
      </div>

      <p style={{ ...labelStyle, margin: short ? "12px 0 7px" : "16px 0 7px" }}>What do you want to use DocuSensa for?</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8 }}>
        {USES.map((u) => (
          <button
            key={u.id}
            type="button"
            aria-pressed={use === u.id}
            onClick={() => onChange({ use: u.id })}
            style={{
              textAlign: "left",
              padding: "11px 12px",
              borderRadius: 12,
              cursor: "pointer",
              transition: "all .16s ease",
              ...(use === u.id
                ? { background: "#F6EEF8", border: "1.5px solid #8B55DD", boxShadow: "0 0 0 3px rgba(139,85,221,.12)" }
                : { background: "#fff", border: "1.5px solid rgba(0,0,0,.10)" }),
            }}
          >
            <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#171717" }}>{u.title}</span>
            {!mobile && (
              <span style={{ display: "block", marginTop: 3, fontSize: 11.5, fontWeight: 500, color: "#8B7D91", lineHeight: 1.3 }}>
                {u.sub}
              </span>
            )}
          </button>
        ))}
      </div>

      <button
        type="button"
        role="checkbox"
        aria-checked={updates}
        onClick={() => onChange({ updates: !updates })}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          width: "100%",
          marginTop: 16,
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid rgba(0,0,0,.07)",
          background: "rgba(255,255,255,.6)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            flex: "none",
            width: 20,
            height: 20,
            marginTop: 1,
            borderRadius: 6,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "#fff",
            transition: "all .15s",
            ...(updates
              ? { background: "#8B55DD", border: "1.5px solid #8B55DD" }
              : { background: "#fff", border: "1.5px solid rgba(0,0,0,.2)" }),
          }}
        >
          {updates ? "✓" : ""}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.45, color: "#5C5060" }}>
          {mobile ? "Send me product updates and tips." : "Send me product updates and tips. You can unsubscribe anytime."}
        </span>
      </button>

      {error && <ErrorBanner style={{ marginTop: 14 }}>{error}</ErrorBanner>}

      <div style={{ marginTop: 18 }}>
        <button type="button" onClick={() => canStart && onStart()} style={primaryButton(canStart, H)}>
          {loading && <Spinner />}
          {loading ? "Setting up your workspace…" : "Start my free trial  →"}
        </button>
      </div>
      <button
        type="button"
        className="ds-link-btn"
        onClick={onSkip}
        disabled={loading}
        style={{
          display: "block",
          width: "100%",
          marginTop: 10,
          padding: 8,
          border: "none",
          background: "none",
          cursor: "pointer",
          whiteSpace: "nowrap",
          font: `600 13.5px ${FONT}`,
          color: "#8B55DD",
        }}
      >
        Skip for now
      </button>
    </div>
  );
};

export default StepAbout;
